// Service Catalog — the configurable "what do we bill" settings page.
//
// One row per service line: procedure code, modifiers, category of service,
// payer, skill kind, who may deliver it, unit rules, default rates, and the two
// EVV switches (evv_required, transmit_to_aggregator). Nothing about GAPP,
// COS 971, or S9122/S9123/S9124 is hardcoded anywhere in the scheduling/EVV
// build — it all reads from here — so the app runs for any service scenario.
//
// Rows are never deleted: visits and schedules reference them. Deactivate
// instead; inactive lines stay visible (greyed) so history still resolves.
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import DataTable from '../../components/DataTable';

const SKILL_KINDS = [
  { value: 'skilled', label: 'Skilled (nursing)' },
  { value: 'pss',     label: 'Unskilled / PSS' },
  { value: 'therapy', label: 'Therapy' },
  { value: 'other',   label: 'Other' },
];
const UNIT_BASES = [
  { value: 'hour',         label: 'Per hour' },
  { value: 'quarter_hour', label: 'Per 15 minutes' },
  { value: 'visit',        label: 'Per visit' },
  { value: 'day',          label: 'Per day' },
];
// Netsmart CaregiverType typecodes. RNS is their code for RN.
const EVV_CAREGIVER_TYPES = [
  { value: '',    label: '—' },
  { value: 'RNS', label: 'RN' },
  { value: 'LPN', label: 'LPN' },
  { value: 'CNA', label: 'CNA' },
  { value: 'HHA', label: 'Home Health Aide' },
  { value: 'PSS', label: 'Personal Support' },
  { value: 'OTH', label: 'Other' },
];
const COLOR_TOKENS = [
  { value: 'skilled', label: 'Skilled', css: 'var(--accent)' },
  { value: 'pss',     label: 'PSS',     css: 'var(--copper)' },
  { value: 'therapy', label: 'Therapy', css: 'var(--info)' },
  { value: 'other',   label: 'Neutral', css: 'var(--zone-closed)' },
];
const swatch = (token) => (COLOR_TOKENS.find(c => c.value === token) || COLOR_TOKENS[3]).css;

const EMPTY = {
  code: '', name: '', description: '', skill_kind: 'skilled', color: 'skilled',
  procedure_code: '', modifier1: '', modifier2: '', modifier3: '',
  category_of_service: '', payer_code: 'GDCH', program_code: '', delivery_system: 'FFFS',
  unit_basis: 'hour', round_units_to_whole: true, default_charge_rate: '', default_pay_rate: '',
  caregiver_types: [], evv_caregiver_type: '',
  evv_required: false, transmit_to_aggregator: false, aggregator: 'netsmart',
  requires_diagnosis: true, requires_referring_npi: true,
  axiscare_service_code: '', axiscare_service_aliases: '', visit_source: 'axiscare', sort_order: 100, active: true,
  payable: true,
};

const field = { width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' };
const label = { fontSize: 11, color: 'var(--text3)', marginBottom: 5 };
const section = { fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text3)', margin: '18px 0 10px', paddingBottom: 6, borderBottom: '0.5px solid var(--border)' };

const Flag = ({ on, yes, no }) => (
  <span className={`badge ${on ? 'badge-done' : 'badge-low'}`}>{on ? yes : no}</span>
);

export default function ServiceCatalog() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [fieldRoles, setFieldRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    const [{ data: cat, error: e1 }, { data: roles }] = await Promise.all([
      supabase.from('service_catalog').select('*').order('sort_order').order('name'),
      supabase.from('roles').select('code,label').eq('active', true).eq('work_location', 'field').order('code'),
    ]);
    if (e1) setError(e1.message);
    setRows(cat || []);
    setFieldRoles(roles || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => showInactive ? rows : rows.filter(r => r.active), [rows, showInactive]);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setError(''); setModal(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      ...EMPTY, ...r,
      modifier1: r.modifier1 || '', modifier2: r.modifier2 || '', modifier3: r.modifier3 || '',
      category_of_service: r.category_of_service || '', program_code: r.program_code || '',
      delivery_system: r.delivery_system || '', description: r.description || '',
      default_charge_rate: r.default_charge_rate ?? '', default_pay_rate: r.default_pay_rate ?? '',
      caregiver_types: r.caregiver_types || [], evv_caregiver_type: r.evv_caregiver_type || '',
      aggregator: r.aggregator || 'netsmart', axiscare_service_code: r.axiscare_service_code || '',
      axiscare_service_aliases: (r.axiscare_service_aliases || []).join(', '),
      color: r.color || 'other', visit_source: r.visit_source || 'axiscare',
    });
    setError('');
    setModal(true);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleType = (code) => setForm(f => ({
    ...f, caregiver_types: f.caregiver_types.includes(code)
      ? f.caregiver_types.filter(c => c !== code) : [...f.caregiver_types, code],
  }));

  const validate = () => {
    if (!form.code.trim()) return 'Code is required (short internal key, e.g. RN_SKILLED).';
    if (!/^[A-Z0-9_]+$/.test(form.code.trim())) return 'Code may only contain capital letters, digits and underscores.';
    if (!form.name.trim()) return 'Name is required.';
    if (!form.procedure_code.trim()) return 'Procedure code (HCPCS/CPT) is required.';
    if (!form.payer_code.trim()) return 'Payer is required.';
    if (form.transmit_to_aggregator && !form.evv_required) return 'A line cannot transmit to the aggregator unless EVV is required on it.';
    if (form.caregiver_types.length === 0) return 'Pick at least one caregiver type that may deliver this service.';
    return '';
  };

  const nullIfBlank = (v) => (v === '' || v === undefined ? null : v);
  const numOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

  const handleSave = async () => {
    const problem = validate();
    if (problem) { setError(problem); return; }
    setSaving(true); setError('');
    const payload = {
      code: form.code.trim().toUpperCase(),
      name: form.name.trim(),
      description: nullIfBlank(form.description.trim()),
      skill_kind: form.skill_kind,
      color: form.color,
      procedure_code: form.procedure_code.trim().toUpperCase(),
      modifier1: nullIfBlank(form.modifier1.trim().toUpperCase()),
      modifier2: nullIfBlank(form.modifier2.trim().toUpperCase()),
      modifier3: nullIfBlank(form.modifier3.trim().toUpperCase()),
      category_of_service: nullIfBlank(form.category_of_service.trim()),
      payer_code: form.payer_code.trim().toUpperCase(),
      program_code: nullIfBlank(form.program_code.trim()),
      delivery_system: nullIfBlank(form.delivery_system.trim().toUpperCase()),
      unit_basis: form.unit_basis,
      round_units_to_whole: !!form.round_units_to_whole,
      payable: form.payable !== false,
      default_charge_rate: numOrNull(form.default_charge_rate),
      default_pay_rate: numOrNull(form.default_pay_rate),
      caregiver_types: form.caregiver_types,
      evv_caregiver_type: nullIfBlank(form.evv_caregiver_type),
      evv_required: !!form.evv_required,
      transmit_to_aggregator: !!form.transmit_to_aggregator,
      aggregator: nullIfBlank(form.aggregator.trim()),
      requires_diagnosis: !!form.requires_diagnosis,
      requires_referring_npi: !!form.requires_referring_npi,
      axiscare_service_code: nullIfBlank(form.axiscare_service_code.trim()),
      axiscare_service_aliases: form.axiscare_service_aliases.split(',').map(a => a.trim()).filter(Boolean),
      visit_source: form.visit_source,
      sort_order: Number(form.sort_order) || 100,
      active: !!form.active,
    };
    const { error: err } = editing
      ? await supabase.from('service_catalog').update(payload).eq('id', editing.id)
      : await supabase.from('service_catalog').insert([{ ...payload, created_by: user?.id || null }]);
    setSaving(false);
    if (err) {
      setError(err.code === '23505' ? `A service line with code ${payload.code} already exists.` : err.message);
      return;
    }
    setModal(false);
    load();
  };

  const toggleActive = async (r) => {
    const { error: err } = await supabase.from('service_catalog').update({ active: !r.active }).eq('id', r.id);
    if (err) { setError(err.message); return; }
    load();
  };

  const columns = useMemo(() => [
    {
      accessorKey: 'name', header: 'Service line', size: 240,
      meta: { mobile: 'primary' },
      cell: ({ row }) => {
        const r = row.original;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: r.active ? 1 : 0.5 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: swatch(r.color), flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 500 }}>{r.name}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{r.code}</div>
            </div>
          </div>
        );
      },
    },
    {
      id: 'procedure', header: 'Procedure', size: 130, meta: { mono: true },
      accessorFn: r => [r.procedure_code, r.modifier1, r.modifier2, r.modifier3].filter(Boolean).join('-'),
    },
    { accessorKey: 'category_of_service', header: 'COS', size: 70, meta: { mono: true }, cell: ({ getValue }) => getValue() || '—' },
    { accessorKey: 'payer_code', header: 'Payer', size: 80, meta: { mono: true } },
    {
      accessorKey: 'skill_kind', header: 'Kind', size: 110,
      cell: ({ getValue }) => (SKILL_KINDS.find(k => k.value === getValue()) || {}).label || getValue(),
    },
    {
      id: 'staff', header: 'Delivered by', size: 130,
      accessorFn: r => (r.caregiver_types || []).join(', '),
      cell: ({ row }) => (row.original.caregiver_types || []).join(', ') || '—',
    },
    {
      accessorKey: 'default_charge_rate', header: 'Charge', size: 90, meta: { align: 'right' },
      cell: ({ getValue }) => getValue() == null ? '—' : `$${Number(getValue()).toFixed(2)}`,
    },
    {
      id: 'evv', header: 'EVV', size: 200, enableSorting: false,
      cell: ({ row }) => {
        const r = row.original;
        return (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <Flag on={r.evv_required} yes="EVV required" no="EVV exempt" />
            {r.evv_required && <Flag on={r.transmit_to_aggregator} yes={`Sends to ${r.aggregator || 'aggregator'}`} no="Not transmitting" />}
            {r.payable === false && <Flag on={false} yes="" no="Not paid in payroll" />}
          </div>
        );
      },
    },
    { accessorKey: 'axiscare_service_code', header: 'AxisCare code', size: 120, meta: { mono: true, mobile: 'hidden' }, cell: ({ getValue }) => getValue() || '—' },
    {
      accessorKey: 'visit_source', header: 'Visits from', size: 120,
      cell: ({ getValue }) => getValue() === 'caera'
        ? <span className="badge badge-done">CAERA</span>
        : <span className="badge badge-low">AxisCare</span>,
    },
    {
      id: 'actions', header: '', size: 150, enableSorting: false, meta: { mobile: 'hidden' },
      cell: ({ row }) => {
        const r = row.original;
        return (
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
            <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => openEdit(r)}>Edit</button>
            <button className="btn" style={{ padding: '4px 10px', fontSize: 12, color: r.active ? 'var(--danger)' : 'var(--success)' }} onClick={() => toggleActive(r)}>
              {r.active ? 'Deactivate' : 'Reactivate'}
            </button>
          </div>
        );
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const transmitting = rows.filter(r => r.active && r.transmit_to_aggregator).length;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, color: 'var(--text3)', maxWidth: 620, lineHeight: 1.6 }}>
          Every service line this agency schedules and bills. Procedure codes, modifiers, category of service,
          payer, and the EVV switches live here, not in code, so the same app runs for any program.
          Scheduling, clock-in, billing runs, and the aggregator feed all read from this table.
          {transmitting === 0 && (
            <div style={{ marginTop: 6, color: 'var(--text2)' }}>
              No line is currently transmitting to a state aggregator.
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
          <label style={{ fontSize: 12, color: 'var(--text3)', display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} /> Show inactive
          </label>
          <button onClick={openAdd} className="btn btn-primary">+ Add service line</button>
        </div>
      </div>

      {error && !modal && (
        <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8,
          padding: '10px 14px', marginBottom: 12, fontSize: 12, color: 'var(--danger)' }}>⚠ {error}</div>
      )}

      <div className="card">
        <DataTable
          tableId="service-catalog"
          columns={columns}
          data={visible}
          getRowId={r => r.id}
          onRowClick={openEdit}
          loading={loading}
          emptyMessage="No service lines yet. Add the first one to start scheduling."
        />
      </div>

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '32px 16px', overflowY: 'auto' }}>
          <div style={{ background: 'var(--card-bg)', borderRadius: 12, width: '100%', maxWidth: 640, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '16px 20px', borderBottom: '0.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{editing ? `Edit ${editing.name}` : 'Add service line'}</div>
              <button onClick={() => setModal(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text3)', lineHeight: 1 }} aria-label="Close">×</button>
            </div>
            <div style={{ padding: '4px 20px 20px' }}>
              {error && (
                <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 7,
                  padding: '10px 14px', margin: '14px 0 4px', fontSize: 12, color: 'var(--danger)' }}>{error}</div>
              )}

              <div style={section}>Identity</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
                <div>
                  <div style={label}>Code</div>
                  <input style={{ ...field, fontFamily: 'var(--mono)' }} value={form.code} disabled={!!editing}
                    onChange={e => set('code', e.target.value.toUpperCase())} placeholder="RN_SKILLED" />
                </div>
                <div>
                  <div style={label}>Name</div>
                  <input style={field} value={form.name} onChange={e => set('name', e.target.value)} placeholder="RN – Skilled Nursing" />
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <div style={label}>Description</div>
                <input style={field} value={form.description} onChange={e => set('description', e.target.value)} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 12 }}>
                <div>
                  <div style={label}>Kind</div>
                  <select style={field} value={form.skill_kind} onChange={e => set('skill_kind', e.target.value)}>
                    {SKILL_KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
                  </select>
                </div>
                <div>
                  <div style={label}>Calendar colour</div>
                  <select style={field} value={form.color} onChange={e => set('color', e.target.value)}>
                    {COLOR_TOKENS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <div style={label}>Sort order</div>
                  <input type="number" style={field} value={form.sort_order} onChange={e => set('sort_order', e.target.value)} />
                </div>
              </div>

              <div style={section}>Billing</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 1fr', gap: 12 }}>
                <div>
                  <div style={label}>Procedure code</div>
                  <input style={{ ...field, fontFamily: 'var(--mono)' }} value={form.procedure_code} onChange={e => set('procedure_code', e.target.value.toUpperCase())} placeholder="S9123" />
                </div>
                <div><div style={label}>Modifier 1</div><input style={{ ...field, fontFamily: 'var(--mono)' }} value={form.modifier1} onChange={e => set('modifier1', e.target.value)} placeholder="U2" /></div>
                <div><div style={label}>Modifier 2</div><input style={{ ...field, fontFamily: 'var(--mono)' }} value={form.modifier2} onChange={e => set('modifier2', e.target.value)} /></div>
                <div><div style={label}>Modifier 3</div><input style={{ ...field, fontFamily: 'var(--mono)' }} value={form.modifier3} onChange={e => set('modifier3', e.target.value)} /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginTop: 12 }}>
                <div>
                  <div style={label}>Category of service</div>
                  <input style={{ ...field, fontFamily: 'var(--mono)' }} value={form.category_of_service} onChange={e => set('category_of_service', e.target.value)} placeholder="971" />
                </div>
                <div>
                  <div style={label}>Payer</div>
                  <input style={{ ...field, fontFamily: 'var(--mono)' }} value={form.payer_code} onChange={e => set('payer_code', e.target.value)} placeholder="GDCH" />
                </div>
                <div>
                  <div style={label}>Program</div>
                  <input style={{ ...field, fontFamily: 'var(--mono)' }} value={form.program_code} onChange={e => set('program_code', e.target.value)} placeholder="leave blank for GA" />
                </div>
                <div>
                  <div style={label}>Delivery system</div>
                  <input style={{ ...field, fontFamily: 'var(--mono)' }} value={form.delivery_system} onChange={e => set('delivery_system', e.target.value)} placeholder="FFFS" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 12 }}>
                <div>
                  <div style={label}>Unit basis</div>
                  <select style={field} value={form.unit_basis} onChange={e => set('unit_basis', e.target.value)}>
                    {UNIT_BASES.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                  </select>
                </div>
                <div>
                  <div style={label}>Default charge rate</div>
                  <input type="number" step="0.01" min="0" style={field} value={form.default_charge_rate} onChange={e => set('default_charge_rate', e.target.value)} />
                </div>
                <div>
                  <div style={label}>Default pay rate</div>
                  <input type="number" step="0.01" min="0" style={field} value={form.default_pay_rate} onChange={e => set('default_pay_rate', e.target.value)} />
                </div>
              </div>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, fontSize: 12, color: 'var(--text2)', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.round_units_to_whole} onChange={e => set('round_units_to_whole', e.target.checked)} />
                Round billed units to whole numbers (required by the Georgia aggregator)
              </label>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, fontSize: 12, color: 'var(--text2)', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.payable !== false} onChange={e => set('payable', e.target.checked)} />
                Paid through payroll runs — untick for a line the caregiver's role already covers (supervisory visits)
              </label>

              <div style={section}>Who may deliver it</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {fieldRoles.length === 0 && <span style={{ fontSize: 12, color: 'var(--text3)' }}>No field roles found in the roles table.</span>}
                {fieldRoles.map(r => {
                  const on = form.caregiver_types.includes(r.code);
                  return (
                    <button key={r.code} type="button" onClick={() => toggleType(r.code)}
                      className="btn" style={{ padding: '5px 12px', fontSize: 12,
                        background: on ? 'var(--accent)' : 'var(--bg2)', color: on ? 'var(--white)' : 'var(--text2)',
                        border: on ? 'none' : '1px solid var(--border)' }}>
                      {r.code} · {r.label}
                    </button>
                  );
                })}
              </div>
              <div style={{ marginTop: 12, maxWidth: 260 }}>
                <div style={label}>Aggregator caregiver type</div>
                <select style={field} value={form.evv_caregiver_type} onChange={e => set('evv_caregiver_type', e.target.value)}>
                  {EVV_CAREGIVER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>

              <div style={section}>Electronic visit verification</div>
              <div style={{ display: 'grid', gap: 8 }}>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: 'var(--text2)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.evv_required}
                    onChange={e => setForm(f => ({ ...f, evv_required: e.target.checked, transmit_to_aggregator: e.target.checked && f.transmit_to_aggregator }))} />
                  EVV required — visits on this line must be clocked with GPS and pass verification before billing
                </label>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: form.evv_required ? 'var(--text2)' : 'var(--text3)', cursor: form.evv_required ? 'pointer' : 'not-allowed' }}>
                  <input type="checkbox" disabled={!form.evv_required} checked={form.transmit_to_aggregator} onChange={e => set('transmit_to_aggregator', e.target.checked)} />
                  Transmit visits to the state aggregator
                  <input style={{ ...field, width: 120, padding: '4px 8px', fontSize: 12, fontFamily: 'var(--mono)' }} value={form.aggregator} disabled={!form.evv_required}
                    onChange={e => set('aggregator', e.target.value)} />
                </label>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: 'var(--text2)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.requires_diagnosis} onChange={e => set('requires_diagnosis', e.target.checked)} />
                  A visit must carry an ICD-10 diagnosis code
                </label>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: 'var(--text2)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.requires_referring_npi} onChange={e => set('requires_referring_npi', e.target.checked)} />
                  A visit must carry the referring physician's NPI
                </label>
              </div>

              <div style={section}>Integration</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <div style={label}>AxisCare service code (shadow phase)</div>
                  <input style={{ ...field, fontFamily: 'var(--mono)' }} value={form.axiscare_service_code} onChange={e => set('axiscare_service_code', e.target.value)} placeholder="RN - Skilled" />
                  <div style={{ ...label, marginTop: 8 }}>Older AxisCare names for this line (comma-separated, used by AxisCare Import)</div>
                  <input style={{ ...field, fontFamily: 'var(--mono)' }} value={form.axiscare_service_aliases} onChange={e => set('axiscare_service_aliases', e.target.value)} placeholder="Skilled-RN" />
                </div>
                <div>
                  <div style={label}>Visits for this line come from</div>
                  <select style={field} value={form.visit_source} onChange={e => set('visit_source', e.target.value)}>
                    <option value="axiscare">AxisCare (calendar + clock-ins)</option>
                    <option value="caera">CAERA (native, verified visits)</option>
                  </select>
                </div>
              </div>
              {form.visit_source === 'caera' && (
                <div style={{ marginTop: 10, fontSize: 12, color: 'var(--warn)', background: 'var(--warn-bg)', border: '0.5px solid var(--warn-border)', borderRadius: 7, padding: '8px 12px' }}>
                  Cut-over switch. Billing runs, payroll, revenue, reports and every visit view will read CAERA's verified visits for this line and ignore AxisCare's copies of it. Make sure every schedule on this line exists in CAERA first.
                </div>
              )}
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: 'var(--text2)', cursor: 'pointer', marginTop: 12 }}>
                <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} />
                Active (available for new schedules)
              </label>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 22 }}>
                <button onClick={() => setModal(false)} className="btn">Cancel</button>
                <button onClick={handleSave} className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : editing ? 'Save changes' : 'Add service line'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
